import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Network, CreditCard, ChevronRight, Loader2,
  Layers, Award,
} from "lucide-react";
import { getAuthHeaders, isPartner } from "../lib/adminAuth";
import AdminTopNav from "../components/AdminTopNav";
import { PartnerFunnel } from "../components/PartnerFunnel";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const fmtMoney = (v) => `$${Math.round(v || 0).toLocaleString("de-CH")}`;

export default function PartnerDownline() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());

  useEffect(() => {
    if (!isPartner()) {
      navigate("/partner/login");
      return;
    }
    axios
      .get(`${API}/partner/downline`, { headers: getAuthHeaders() })
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Konnte Downline nicht laden.");
        if (e?.response?.status === 401) navigate("/partner/login");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  // Flatten the downline into a depth-first, indented list. Each row knows
  // its depth and whether any ancestor in `collapsed` hides it. This avoids
  // self-referential React components (which babel-traverse couldn't handle).
  const rows = useMemo(() => {
    if (!data?.downline?.length) return [];
    const childrenByParent = new Map();
    const minDepth = Math.min(...data.downline.map((p) => p.depth || 1));
    for (const p of data.downline) {
      const key = (p.depth || 1) === minDepth ? "__ROOT__" : p.parent_id;
      if (!childrenByParent.has(key)) childrenByParent.set(key, []);
      childrenByParent.get(key).push(p);
    }
    for (const arr of childrenByParent.values()) {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"));
    }
    const out = [];
    const walk = (parentKey, depth, hiddenByAncestor) => {
      const kids = childrenByParent.get(parentKey) || [];
      for (const k of kids) {
        const grandKids = childrenByParent.get(k.id) || [];
        out.push({
          ...k,
          _depth: depth,
          _hasChildren: grandKids.length > 0,
          _hidden: hiddenByAncestor,
        });
        const hideChildren = hiddenByAncestor || collapsed.has(k.id);
        walk(k.id, depth + 1, hideChildren);
      }
    };
    walk("__ROOT__", 0, false);
    return out.filter((r) => !r._hidden);
  }, [data, collapsed]);

  const toggle = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="partner-downline-page">
      <AdminTopNav />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 space-y-8">
        <header>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Mein Bereich</div>
          <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight">Meine Struktur</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Übersicht über deine eigenen Kunden, deine Downline und das aggregierte Kundengesamtvolumen.
          </p>
        </header>

        {loading ? (
          <div className="al-card p-12 flex items-center justify-center text-slate-500" data-testid="downline-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Downline …
          </div>
        ) : !data ? null : (
          <>
            {/* Summary is ALWAYS shown — even without downline, so partners with
                only their own customers see their volume. */}
            <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="downline-summary">
              <SummaryCard
                icon={<Users className="h-5 w-5" />}
                label="Eigene Kunden"
                value={data.summary.own_customers ?? 0}
                testid="downline-stat-own-customers"
              />
              <SummaryCard
                icon={<Layers className="h-5 w-5" />}
                label="Downline-Partner"
                value={data.summary.partners ?? 0}
                testid="downline-stat-partners"
              />
              <SummaryCard
                icon={<Network className="h-5 w-5" />}
                label="Kunden in Downline"
                value={data.summary.customers ?? 0}
                testid="downline-stat-customers"
              />
              <SummaryCard
                icon={<CreditCard className="h-5 w-5" />}
                label="Kundengesamtvolumen"
                value={fmtMoney(data.summary.total_balance)}
                testid="downline-stat-volume"
                highlight
              />
            </section>

            {/* Funnel — wo verliert der Partner Kunden? */}
            <PartnerFunnel />

            {(data.downline?.length || 0) === 0 ? (
              <div className="al-card p-10 text-center" data-testid="downline-empty">
                <Network className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <h2 className="font-display font-bold text-xl mb-2">Noch keine Downline</h2>
                <p className="text-sm text-slate-600 max-w-md mx-auto">
                  Sobald du jemanden einlädst, der über deinen Reflink Partner wird, taucht
                  er hier auf — inklusive seiner eigenen Kunden und seiner Downline.
                </p>
                <Link to="/partner/links" className="inline-flex items-center gap-2 mt-5 text-sm font-display font-semibold text-violet-600 hover:text-violet-800">
                  Deinen Reflink anzeigen <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <>

            <section className="al-card overflow-hidden" data-testid="downline-tree">
              <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-violet-50/40 to-transparent">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                  <Network className="h-3.5 w-3.5 text-[var(--al-purple)]" /> Hierarchie
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Alle Partner, die direkt oder indirekt über dich vermittelt wurden.
                  Die personenbezogenen Daten der Kunden sind für dich nicht einsehbar — nur Anzahl und aggregiertes Volumen.
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {rows.map((node) => (
                  <div
                    key={node.id}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/60 transition-colors"
                    data-testid={`tree-node-${node.slug}`}
                  >
                    <div style={{ width: node._depth * 22 }} className="flex-shrink-0" />
                    {node._hasChildren ? (
                      <button
                        type="button"
                        onClick={() => toggle(node.id)}
                        className="h-5 w-5 flex-shrink-0 rounded hover:bg-violet-100 text-slate-500"
                        aria-label={collapsed.has(node.id) ? "Ausklappen" : "Einklappen"}
                      >
                        <ChevronRight className={`h-4 w-4 transition-transform ${collapsed.has(node.id) ? "" : "rotate-90"}`} />
                      </button>
                    ) : (
                      <span className="h-5 w-5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display font-semibold text-slate-800 truncate">
                          {node.name || "Unbenannt"}
                        </span>
                        {node._depth === 0 ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">
                            <Award className="h-3 w-3" /> Direkt
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">
                            Level {node.depth}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono-tab text-slate-400 truncate">{node.slug}</div>
                      <div className="sm:hidden mt-1 text-xs text-slate-600 flex flex-wrap items-center gap-2">
                        <span>{node.direct_customers} Kd.</span>
                        <span className="text-slate-300">·</span>
                        <span>{node.direct_partners} Sub</span>
                        <span className="text-slate-300">·</span>
                        <span className="font-display font-semibold text-slate-800">{fmtMoney(node.total_balance)}</span>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-5 flex-shrink-0">
                      <div className="flex items-center gap-1 text-xs text-slate-600">
                        <Users className="h-3 w-3" />
                        <span className="tabular-nums font-display font-semibold text-slate-800">{node.direct_customers}</span>
                        <span className="uppercase tracking-widest text-[10px] text-slate-400">Kd.</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-600">
                        <Layers className="h-3 w-3" />
                        <span className="tabular-nums font-display font-semibold text-slate-800">{node.direct_partners}</span>
                        <span className="uppercase tracking-widest text-[10px] text-slate-400">Sub</span>
                      </div>
                      <div className="text-right tabular-nums w-28">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Volumen</div>
                        <div className="font-display font-bold text-sm text-slate-800">{fmtMoney(node.total_balance)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function SummaryCard({ icon, label, value, testid, highlight }) {
  return (
    <div className={`al-card p-5 ${highlight ? "ring-2 ring-violet-200 bg-gradient-to-br from-violet-50/60 to-white" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
        {icon} <span>{label}</span>
      </div>
      <div className={`mt-2 font-display font-bold text-3xl tracking-tight ${highlight ? "al-grad-text" : ""}`}>{value ?? 0}</div>
    </div>
  );
}
